import { FieldLabel } from './FieldLabel'
import { ReadValue } from './read/ReadValue'
import { SeparatorRow } from './presentational/SeparatorRow'
import { SpacerRow } from './presentational/SpacerRow'
import { InstructionBox } from './presentational/InstructionBox'
import { HeaderRow } from './presentational/HeaderRow'
import { inputDispatch } from './dispatch'
import type { FieldDefinition } from '../../types'
import type { FieldInputProps } from './types'

type Props = {
  field: FieldDefinition
  value: unknown
  mode: 'READ' | 'EDIT'
  onChange?: (value: unknown) => void
  disabled?: boolean
  /** Wymagany dla pól FILE/GALLERY i dla FieldAnnexMarker w labelu. */
  contractId?: number
  /** Slot obok labela - F-VER ptaszek weryfikacji, opcjonalnie inne badge w przyszłości. */
  verificationBadge?: React.ReactNode
}

/**
 * Cienki router: typy prezentacyjne (SEPARATOR/SPACER/INSTRUCTION/HEADER) zwracają
 * dedykowane komponenty z presentational/. Pozostałe wybierają input z {@link inputDispatch}
 * w EDIT mode lub {@link ReadValue} w READ mode.
 */
export function FieldRenderer({ field, value, mode, onChange, disabled, contractId, verificationBadge }: Props) {
  switch (field.fieldType) {
    case 'SEPARATOR':   return <SeparatorRow labelPl={field.labelPl} />
    case 'SPACER':      return <SpacerRow />
    case 'INSTRUCTION': return <InstructionBox labelPl={field.labelPl} helpText={field.helpText ?? null} />
    case 'HEADER':      return <HeaderRow labelPl={field.labelPl} helpText={field.helpText ?? null} />
  }

  const isReadOnly = mode !== 'EDIT' || !onChange
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel field={field} contractId={contractId} slot={verificationBadge} />
      <div className="min-w-0">
        {isReadOnly
          ? <ReadValue field={field} value={value} contractId={contractId} />
          : <DispatchedInput field={field} value={value} onChange={onChange!} disabled={disabled} contractId={contractId} />}
      </div>
    </div>
  )
}

function DispatchedInput(props: FieldInputProps) {
  // TS nie zna narrowingu sprzed router-switcha przez wywołanie funkcji, ale {@code satisfies}
  // w dispatch.ts gwarantuje że dla każdego EditableFieldType wpis istnieje. Cast keyof
  // typeof inputDispatch jest type-only, runtime bezpieczny.
  const Input = inputDispatch[props.field.fieldType as keyof typeof inputDispatch]
  if (!Input) return null
  return <Input {...props} />
}
